import { useCallback } from 'react';

import Caption from '@zenbusiness/common-components/Caption';
import { generateUrl } from '@zenbusiness/common-components/SharedAsset';
import Text from '@zenbusiness/common-components/Text';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

import { BorderedBox } from '#client/pages/EducationStudio/components/BorderedBox';
import BubblePicker from '#client/pages/EducationStudio/components/BubblePicker';
import {
  ActivityBox,
  BubblePickerWrapper, ButtonPrimary, LeftContainer,
  NextContainer
} from '#client/pages/EducationStudio/components/CustomPage/Waltz/WaltzActivityPage';
import WaltzActivitySection
  from '#client/pages/EducationStudio/components/Interactive/WaltzActivitySection';
import LessonTitle from '#client/pages/EducationStudio/components/LessonTitle';
import { StandardImage } from '#client/pages/EducationStudio/components/multimedia/image/StandardImage';
import { useLessonStorage } from '#client/pages/EducationStudio/hooks';
import { useBreakpoints } from '#client/pages/EducationStudio/hooks/useBreakpoints';

const StyledActivityContainer = styled(Box)(({ theme }) => ({
  maxWidth: '64rem',
  width: '100vw',
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridColumnGap: '5%',
  justifyContent: 'center',
  marginTop: '0',
  margin: 'auto 1.5rem',
  gridRowGap: '1.5rem',
  [theme.breakpoints.up('sm')]: {
    gridRowGap: '1rem',
    gridTemplateColumns: '1.5fr 2fr'
  }
}));

const StyledStandardImage = styled(StandardImage)({
  width: '100%'
});

const StyledBorderedBox = styled(BorderedBox, {
  shouldForwardProp: (prop) => prop !== 'image'
})(({ img }) => ({
  backgroundImage: `url(${generateUrl(`${img}`, 530, 400, 'auto')})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  gridRow: 'span 10'
}));

const StyledLessonTitle = styled(LessonTitle)({
  '&&': {
    marginBottom: '0'
  }
});

const ActivityPage = ({ onPrimaryClick }) => {
  const { lesson } = useParams();
  const localStorageKey = 'hsp_support_plan';
  const { getStorageValue, setStorageValue, saveChanges } = useLessonStorage(lesson);

  const content = {
    title: 'Let’s create your HSP support plan',
    activities: [
      { text: 'Create art' },
      { text: 'Drink more water' },
      { text: 'Meditate' },
      { text: 'Declutter your space' },
      { text: 'Disconnect from tech' },
      { text: 'Go into nature' },
      { text: 'Take a bath' },
      { text: 'Get a massage' },
      { text: 'Exercise gently' },
      { text: 'Snuggle a pet' },
      { text: 'Dim the lights' },
      { text: 'Listen to music' },
      { text: 'Eat healthy foods' },
      { text: 'Take a nap' },
      { text: 'Take fewer meetings' },
      { text: 'Limit caffeine' },
      { text: 'Light a candle' }
    ],
    maxSelection: 5,
    image: '/illustration/J2S_Spidey_Activity_Image1.svg',
    imageAlt: 'spidey exercise image 1',
    groupColor: '#602E82',
    groupTextColor: '#ffffff',
    groupTitle: 'Support strategies',
    bubbleColor: '#BD8AE0',
    bubbleTextColor: '#000000'
  };

  const {
    sizes: { isMobile }
  } = useBreakpoints();

  const isEmpty = (value) => value === '';

  const getStorageProp = (prop) => {
    const value = getStorageValue(prop);
    if (isEmpty(value)) return [];
    return value;
  };

  const onNextHandler = useCallback(() => {
    saveChanges();
    if (onPrimaryClick) onPrimaryClick();
  }, [saveChanges, onPrimaryClick]);

  const handleBubbleSelection = (option) => {
    const selectedBubbles = getStorageProp(localStorageKey);
    if (selectedBubbles.some((selected) => selected.text === option.text)) {
      setStorageValue(
        localStorageKey,
        selectedBubbles.filter((value) => value.text !== option.text)
      );
    } else if (selectedBubbles.length < content.maxSelection) {
      setStorageValue(localStorageKey, [...selectedBubbles, option]);
    }
  };

  return (
    <Box width="100%" justifyContent="center" display="flex">
      <StyledActivityContainer>
        {!isMobile ? (
          <LeftContainer>
            <StyledStandardImage
              src={content.image}
              width={400}
              alt={content.imageAlt}
            />
          </LeftContainer>
        ) : (
          <StyledBorderedBox img={content.image} />
        )}
        <Box>
          <Caption size="md" bold>EXERCISE</Caption>
          <StyledLessonTitle
            title={content.title}
          />
        </Box>
        <Box>
          <Text size="lg">
            Here are some strategies that tend to help HSPs. Choose
            {' '}
            {content.maxSelection}
            {' '}
            that you’re willing to try.
          </Text>
        </Box>
        <ActivityBox>
          <WaltzActivitySection
            options={getStorageProp(localStorageKey)}
            sectionTitle={content.groupTitle}
            bubbleColor={content.bubbleColor}
            bubbleTextColor={content.bubbleTextColor}
            sectionColor={content.groupColor}
            sectionTextColor={content.groupTextColor}
          />
        </ActivityBox>
        <BubblePickerWrapper>
          <BubblePicker
            options={content.activities}
            selectedBubbles={getStorageProp(localStorageKey)}
            onClick={handleBubbleSelection}
            completed={getStorageProp(localStorageKey).length >= content.maxSelection}
          />
        </BubblePickerWrapper>
        <NextContainer>
          <ButtonPrimary
            onClick={onNextHandler}
            disabled={getStorageProp(localStorageKey).length < content.maxSelection}
            variant="contained"
            color="primary"
            size="large"
          >
            Next
          </ButtonPrimary>
        </NextContainer>
      </StyledActivityContainer>
    </Box>
  );
};

ActivityPage.propTypes = {
  onPrimaryClick: PropTypes.func
};

export default ActivityPage;
